import React, { useState, useEffect, useRef, useMemo } from "react"
import styles from "./HeaderNavImproved.module.scss"
import { connect } from "react-redux"
import Logo from "../../../assets/images/LogoEdify1.png"
import NotificationsImproved from "../../Notifications/NotificationsImproved.tsx"
import { useNavigate, useLocation } from "react-router-dom"
import { ReactComponent as Faq } from "../../../assets/svg/FAQ.svg"
import { ReactComponent as FaqActive } from "../../../assets/svg/FAQActive.svg"
import { ReactComponent as NoImage } from "../../../assets/svg/noImageIcon.svg"
import { ChevronDown, User, LogOut } from "lucide-react"
import {
  closeSuscriptionModal,
  setActivateFreeTrial,
  logout,
  openMainModal,
  closeMainModal,
} from "../../../store/actions"
import Tooltip from "../../UI/ToolTip/ToolTip"
import useUserImage from "../../../hooks/useUserImage"

interface HeaderNavImprovedProps {
  userData: any
  logout: (withAnimation: boolean, navigate: any) => void
  openMainModal: (config: any) => void
  closeMainModal: () => void
  projectDocumentation: any
}

const HeaderNavImproved: React.FC<HeaderNavImprovedProps> = ({
  userData,
  logout,
  openMainModal,
  closeMainModal,
  projectDocumentation,
}) => {
  const [isActive, setIsActive] = useState<string | null>(null)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showToolTip, setShowToolTip] = useState({
    faq: { show: false, text: "Ayuda" },
  })
  
  const navigate = useNavigate()
  const location = useLocation()
  const userMenuRef = useRef<HTMLDivElement>(null)
  
  const { imageError, initials, profileImage } = useUserImage(userData)

  // Detectar si estamos en la ruta de documentación de obra
  const isProjectDocumentationRoute = location.pathname.includes('/documentacion-de-obra')
  
  // Obtener nombres de las selecciones del proyecto
  const projectType = projectDocumentation?.selectedType
  const categoryName = projectDocumentation?.categories?.find((cat: any) => cat.id === projectDocumentation?.selectedCategory)?.name
  const subCategoryName = projectDocumentation?.subCategories?.find((sub: any) => sub.id === projectDocumentation?.selectedSubCategory)?.name
  const useName = projectDocumentation?.uses?.find((use: any) => use.id === projectDocumentation?.selectedUses?.[0])?.name
  const materialityName = projectDocumentation?.materialities?.find((mat: any) => mat.id === projectDocumentation?.selectedMaterialities?.[0])?.name
  
  // Mostrar resumen cuando Redux lo indique (activado por scroll en ProjectForm)
  const showSummary = isProjectDocumentationRoute && projectDocumentation?.showHeaderSummary && (projectType || categoryName || subCategoryName || useName || materialityName)

  const handleClickOutside = (event: MouseEvent) => {
    if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
      setShowUserMenu(false)
    }
  }

  useEffect(() => {
    if (showUserMenu) {
      document.addEventListener("mousedown", handleClickOutside)
    } else {
      document.removeEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showUserMenu])


  const userName = userData?.type === "COMPANY" 
    ? userData?.razon_social 
    : `${userData?.name} ${userData?.last_name}`

  const isViewingOwnProfile = useMemo(() => {
    if (!userData?.id) return false
    const pathParts = location.pathname.split("/").filter(Boolean)
    if (pathParts[0] !== "perfil") return false
    if (!pathParts[1]) return false
    return pathParts[1] === String(userData.id)
  }, [location.pathname, userData?.id])

  const handleRedirectToProfile = () => {
    setShowUserMenu(false)
    navigate(`/perfil/${userData.id}`)
  }

  const handleRedirect = (title: string) => {
    if (title === "FAQ") {
      navigate("/faq")
    }
  }

  const handleCloseSesion = () => {
    setShowUserMenu(false)
    openMainModal({
      title: "¿Deseas cerrar la sesión activa?",
      body: "Dejarán de llegarte las notificaciones de actividad",
      isSuccess: false,
      iconType: "warning",
      confirmHandler: () => {
        logout(true, navigate)
        closeMainModal()
      },
    })
  }

  const handleBackToHome = () => {
    if (location?.pathname !== "/dashboard") {
      navigate("/dashboard")
    }
  }

  const handleTooltip = (tooltip: string) => {
    setShowToolTip((prevState) => ({
      ...prevState,
      [tooltip]: { ...prevState[tooltip], show: !prevState[tooltip].show },
    }))
  }

  useEffect(() => {
    if (location?.pathname === "/faq") {
      setIsActive("FAQ")
    } else if (location?.pathname === "/notificaciones") {
      setIsActive("notificaciones")
    } else if (location?.pathname.startsWith("/perfil")) {
      const pathParts = location.pathname.split("/")
      const idInPath = pathParts[2]
      if (idInPath && String(idInPath) === String(userData.id)) {
        setIsActive("Perfil")
      } else {
        setIsActive(null)
      }
    } else {
      setIsActive(null)
    }
  }, [location?.pathname, userData.id])

  return (
    <nav className={styles.nav}>
      <div className={styles.navBody}>
        <div className={styles.logoContainer} onClick={handleBackToHome}>
          <img src={Logo} alt="Logo" className={styles.logo} />
        </div>

        {/* Resumen de Proyecto - Solo visible en ruta de documentación */}
        {isProjectDocumentationRoute && (projectType || categoryName || subCategoryName || useName || materialityName) && (
          <div className={`${styles.summaryCenter} ${showSummary ? styles.visible : styles.hidden}`}>
            <div className={styles.summaryContent}>
              {projectType && (
                <>
                  <span className={styles.summaryLabel}>Tipo:</span>
                  <span className={styles.summaryValue}>{projectType === 'proyecto' ? 'Proyecto' : 'Anteproyecto'}</span>
                  <span className={styles.summaryDivider}>/</span>
                </>
              )}
              {categoryName && (
                <>
                  <span className={styles.summaryLabel}>Cat:</span>
                  <span className={styles.summaryValue}>{categoryName}</span>
                  <span className={styles.summaryDivider}>/</span>
                </>
              )}
              {subCategoryName && (
                <>
                  <span className={styles.summaryLabel}>Sub:</span>
                  <span className={styles.summaryValue}>{subCategoryName}</span>
                  <span className={styles.summaryDivider}>/</span>
                </>
              )}
              {useName && (
                <>
                  <span className={styles.summaryLabel}>Uso:</span>
                  <span className={styles.summaryValue}>{useName}</span>
                  <span className={styles.summaryDivider}>/</span>
                </>
              )}
              {materialityName && (
                <>
                  <span className={styles.summaryLabel}>Mat:</span>
                  <span className={styles.summaryValue}>{materialityName}</span>
                </>
              )}
            </div>
          </div>
        )}

        <div className={styles.navIcons}>
          <div
            className={`${styles.navIcon} ${isActive === "FAQ" && styles.isActive}`}
            onMouseEnter={() => handleTooltip("faq")}
            onMouseLeave={() => handleTooltip("faq")}
            onClick={() => handleRedirect("FAQ")}
          >
            {isActive === "FAQ" ? (
              <FaqActive width={20} height={20} />
            ) : (
              <Faq width={20} height={20}/>
            )}
            {showToolTip.faq.show && <Tooltip text={showToolTip.faq.text} fromNav={false} fromHeaderNav={true} />}
          </div>

          <NotificationsImproved
            isMobile={false}
          />

          <div className={styles.userMenu} ref={userMenuRef}>
            <button
              className={`${styles.userButton} ${showUserMenu ? styles.active : ""} ${isViewingOwnProfile ? styles.profileActive : ""}`}
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className={styles.userAvatar}>
                {!imageError && profileImage ? (
                  <img
                    className={styles.avatarImage}
                    src={profileImage}
                    alt="user"
                  />
                ) : initials ? (
                  <div className={styles.avatarInitials}>{initials}</div>
                ) : (
                  <NoImage width={24} height={24} />
                )}
              </div>
              <span className={styles.userName}>{userName}</span>
              <ChevronDown size={16} className={styles.chevron} />
            </button>

            {showUserMenu && (
              <div className={styles.userDropdown}>
                <div className={styles.dropdownHeader}>
                  <div className={styles.dropdownAvatar}>
                    {!imageError && profileImage ? (
                      <img src={profileImage} alt="user" />
                    ) : initials ? (
                      <div className={styles.dropdownInitials}>{initials}</div>
                    ) : (
                      <NoImage width={40} height={40} />
                    )}
                  </div>
                  <div className={styles.dropdownUserInfo}>
                    <p className={styles.dropdownName}>{userName}</p>
                    <p className={styles.dropdownEmail}>{userData?.username}</p>
                  </div>
                </div>

                <div className={styles.dropdownDivider} />

                <button
                  className={styles.dropdownItem}
                  onClick={handleRedirectToProfile}
                >
                  <User size={16} />
                  <span>Mi perfil</span>
                </button>

                <div className={styles.dropdownDivider} />

                <button
                  className={styles.dropdownItem}
                  onClick={handleCloseSesion}
                >
                  <LogOut size={16} />
                  <span>Cerrar sesión</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  )
}

const mapStateToProps = (state: any) => {
  return {
    userData: state.general.userData,
    projectDocumentation: state.anteProyecto,
  }
}

export default connect(mapStateToProps, {
  closeSuscriptionModal,
  setActivateFreeTrial,
  logout,
  openMainModal,
  closeMainModal,
})(HeaderNavImproved)

